import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, handleApiError } from '../../../services/api';
import './AppVersionManager.css';

const AppVersionManager = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    message_ar: '',
    message_en: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState(null); // { type: 'success'|'error', text: '' }

  useEffect(() => {
    fetchVersionData();
  }, []);

  const fetchVersionData = async () => {
    setIsLoading(true);
    try {
      const response = await adminAPI.getAppVersion();
      if (response.data && response.data.data) {
        const data = response.data.data;
        setFormData({
          title_ar: data.title_ar || '',
          title_en: data.title_en || '',
          message_ar: data.message_ar || '',
          message_en: data.message_en || '',
        });
      }
    } catch (error) {
      setSubmitMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);
    
    try {
      // Note: We are only sending the update data, NOT the version numbers
      await adminAPI.updateAppVersion(formData);
      setSubmitMessage({ type: 'success', text: t('appVersion.success', 'Update data saved successfully.') });
      
      // clear success message after 5 seconds
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      setSubmitMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="app-version-manager-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className="app-version-manager">
      <div className="composer-header">
        <h3>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
            <path d="M2 2l7.586 7.586"></path>
            <circle cx="11" cy="11" r="2"></circle>
          </svg>
          {t('appVersion.title', 'App Update Information')}
        </h3>
        <p>{t('appVersion.description', 'Manage the message and content shown to users when a new update is available.')}</p>
      </div>

      {submitMessage && (
        <div className={`composer-alert alert-${submitMessage.type}`}>
          {submitMessage.text}
        </div>
      )}

      <form className="composer-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="title_ar">{t('appVersion.titleAr', 'Title (Arabic)')} <span className="required">*</span></label>
            <input
              type="text"
              id="title_ar"
              name="title_ar"
              value={formData.title_ar}
              onChange={handleChange}
              placeholder={t('appVersion.titlePlaceholderAr', 'متوفر تحديث جديد')}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="title_en">{t('appVersion.titleEn', 'Title (English)')} <span className="required">*</span></label>
            <input
              type="text"
              id="title_en"
              name="title_en"
              value={formData.title_en}
              onChange={handleChange}
              placeholder={t('appVersion.titlePlaceholderEn', 'A new update is available')}
              disabled={isSubmitting}
              required
            />
          </div>
        </div>



        <div className="form-group">
          <label htmlFor="message_ar">{t('appVersion.messageAr', 'Message (Arabic)')} <span className="required">*</span></label>
          <textarea
            id="message_ar"
            name="message_ar"
            value={formData.message_ar}
            onChange={handleChange}
            placeholder={t('appVersion.messagePlaceholderAr', 'Write the update message in Arabic...')}
            disabled={isSubmitting}
            required
            rows={5}
          />
        </div>

        <div className="form-group">
          <label htmlFor="message_en">{t('appVersion.messageEn', 'Message (English)')} <span className="required">*</span></label>
          <textarea
            id="message_en"
            name="message_en"
            value={formData.message_en}
            onChange={handleChange}
            placeholder={t('appVersion.messagePlaceholderEn', 'Write the update message in English...')}
            disabled={isSubmitting}
            required
            rows={5}
          />
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="loading-spinner-small"></span>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {t('appVersion.saveBtn', 'Save Update Data')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppVersionManager;
